import type { ConsignmentLineItem } from '../model/types';
import { parseInventoryUnits, parseNumericValue } from './line-units';

/**
 * Weight (kg) and volume (m³) of the units on a pallet, per unit like the legacy
 * widget: a line adds `its weight / its units × its units on the pallet`. Lines
 * not in `lineById`, or without inventory units, add nothing.
 */
export function palletLoad(
  units: Record<string, number>,
  lineById: ReadonlyMap<string, ConsignmentLineItem>
): { weight: number; volume: number } {
  let weight = 0;
  let volume = 0;
  for (const [lineId, count] of Object.entries(units)) {
    const line = lineById.get(lineId);
    const total = parseInventoryUnits(line?.inventoryUnits).quantity;
    if (!line || total <= 0) continue;
    weight += (parseNumericValue(line.weight) / total) * count;
    volume += (parseNumericValue(line.volume) / total) * count;
  }
  return { weight, volume };
}

export type CapacityLevel = 'normal' | 'nearlyFull' | 'full';

/**
 * How full a pallet is against its capacity, with the legacy widget's levels:
 * nearly full from 80%, full from 100%. Without a capacity it is never full.
 * Informational only — an overloaded pallet can still be saved.
 */
export function capacityLevel(current: number, max: number | null): { level: CapacityLevel; percent: number } {
  const fill = max ? current / max : 0;
  const percent = Math.round(fill * 100);
  if (fill >= 1) return { level: 'full', percent };
  if (fill >= 0.8) return { level: 'nearlyFull', percent };
  return { level: 'normal', percent };
}
